import React, { useEffect, useState } from "react";
import { TextField, InputAdornment, IconButton, Button, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Accordion from "react-bootstrap/Accordion";
import moment from "moment";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import SearchIcon from "@mui/icons-material/Search";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { downloadLink } from "../../helpers/constFIles";
import { matchesSearch } from "../../helpers/search";
import AttendanceCalendar from "./AttendanceCalendar";
import AttendanceLog from "./AttendanceLog";
import LocationPin from "./LocationPin";
import { holidayKeys, hm, timeOf, STATUS, apiDate, defaultRange } from "./helpers";
import "./Attendance.scss";

const SEARCH_FIELDS = ["employeeName", "employeeCode", "designation"];

const initials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : "") +
        (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

/* The last fortnight as a row of squares — the shape of someone's month reads
   faster than any number, and it is what the collapsed row is scanned by. */
const Streak = ({ rows }) => (
    <span className="att-streak">
        {rows.filter((row) => row.status !== "upcoming").slice(-14).map((row) => (
            <Tooltip key={row.dateKey} placement="top" arrow
                title={`${moment(row.date).format("ddd DD MMM")} · ${(STATUS[row.status] || {}).label || row.status}`}>
                <span className={`att-tick att-tick--${row.status}`} />
            </Tooltip>
        ))}
    </span>
);

/* the most recent day this person actually punched, so the collapsed row can
   say when and where they were last seen */
const lastPunch = (rows = []) => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
        if (rows[i].checkIn) return rows[i];
    }
    return null;
};

const TeamAttendance = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [search, setSearch] = useState("");

    /* the same current month My Attendance opens on */
    const [range, setRange] = useState(defaultRange);
    const [draft, setDraft] = useState(range);

    const load = async (period = range) => {
        setLoading(true);
        const resp = await fetchData({
            url: `${URLS.teamAttendance}?from=${apiDate(period.from)}&to=${apiDate(period.to)}`
                + `&holidays=${encodeURIComponent(holidayKeys())}`,
            method: "get",
        });
        setLoading(false);
        if (resp?.success === false) {
            toast.error(resp?.message || "Could not load the team's attendance");
            return;
        }
        setMembers(resp.data?.members || []);
    };

    useEffect(() => {
        load(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.from, range.to]);

    const result = members.filter((m) => matchesSearch(m, search, SEARCH_FIELDS));

    if (loading) return <SkeletonLoader />;

    if (members.length === 0) {
        return <NoData
            title="No team to show"
            message="Attendance for the people who report to you will appear here." />;
    }

    return (
        <div className="att-page">
            <div className="wh-toolbar">
                <h6 className="wh-section-title" style={{ margin: 0 }}>
                    Team Attendance · {members.length} {members.length === 1 ? "person" : "people"}
                </h6>

                <div className="wh-toolbar-controls wh-toolbar-controls--end">
                    <TextField
                        placeholder="Search name, code or role…"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="wh-search-field"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size="small" className="wh-search-clear"
                                        aria-label="Clear search" onClick={() => setSearch("")}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />
                </div>
            </div>

            <div className="wh-filter-bar att-range-bar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="From"
                        inputFormat="DD/MM/YYYY"
                        value={draft.from}
                        maxDate={draft.to}
                        onChange={(date) => {
                            const value = date && date.toDate ? date.toDate() : date;
                            setDraft((prev) => ({ ...prev, from: value }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="To"
                        inputFormat="DD/MM/YYYY"
                        value={draft.to}
                        minDate={draft.from}
                        onChange={(date) => {
                            const value = date && date.toDate ? date.toDate() : date;
                            setDraft((prev) => ({ ...prev, to: value }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                    />
                </LocalizationProvider>

                <Button variant="contained" startIcon={<SearchRoundedIcon />}
                    onClick={() => setRange({ ...draft })}>Show</Button>

                <Button variant="outlined" className="wh-filter-bar-end"
                    onClick={() => { const next = defaultRange(); setDraft(next); setRange(next); }}>
                    This month
                </Button>
            </div>

            {result.length === 0
                ? <NoData title="Nobody matches that" message="Try a different name, code or role." />
                : <Accordion className="wh-accordion wh-accordion-people">
                    {result.map((member, index) => {
                        const seen = lastPunch(member.rows);

                        return (
                            <Accordion.Item key={member.employeeCode} eventKey={String(index)}>
                                <Accordion.Header>
                                    <span className="wh-person">
                                        {member.profilePic
                                            ? <img className="wh-avatar wh-avatar-sm"
                                                src={downloadLink + member.profilePic} alt=""
                                                onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                            : null}
                                        <span className="wh-avatar wh-avatar-sm">{initials(member.employeeName)}</span>
                                        <span className="att-person">
                                            <b>{member.employeeName}</b>
                                            <em>{member.designation || member.employeeCode}</em>
                                        </span>
                                    </span>

                                    <span className="att-member-meta">
                                        <Streak rows={member.rows} />

                                        {seen && (
                                            <span className="att-last-seen">
                                                <em>Last in</em>
                                                {moment(seen.date).format("DD MMM")} · {timeOf(seen.checkIn)}
                                                <LocationPin location={seen.checkInLocation} label="Checked in" />
                                            </span>
                                        )}

                                        <span className="wh-pill wh-pill-good">
                                            {member.summary.presentDays + member.summary.partialDays} present
                                        </span>
                                        <span className={`wh-pill ${member.summary.absentDays ? "wh-pill-danger" : "wh-pill-quiet"}`}>
                                            {member.summary.absentDays} absent
                                        </span>
                                        <span className={`wh-pill ${member.summary.leaveDays ? "wh-pill" : "wh-pill-quiet"}`}>
                                            {member.summary.leaveDays} leave
                                        </span>
                                        <span className="wh-pill wh-pill-quiet">
                                            {hm(member.summary.avgMinutesPerDay)} avg
                                        </span>
                                    </span>
                                </Accordion.Header>

                                <Accordion.Body>
                                    <AttendanceCalendar rows={member.rows} />
                                    <div className="att-member-log">
                                        <AttendanceLog rows={member.rows} shift={member.summary.shift} />
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>}
        </div>
    );
};

export default TeamAttendance;
